import type {
  CivicExpedition,
  CivicExpeditionChoice,
  CivicNextGoal,
  ExpeditionWorkstream,
} from './data/tribe-expedition-api';

export interface CivicExpeditionBoard {
  can_start: boolean;
  expeditions: CivicExpedition[];
  choices?: CivicExpeditionChoice[];
  next_goals?: CivicNextGoal[];
}

const roles: readonly ExpeditionWorkstream[] = ['wayfinder', 'scribe', 'builder'];

const families = {
  waterways: {
    label: 'Waterways',
    preparing: 'Preparing the waterways',
    active: 'The waterways are awakening',
  },
  living_archive: {
    label: 'Living Archive',
    preparing: 'Preparing the Living Archive',
    active: 'The archive is taking shape',
  },
  garden_district: {
    label: 'Garden District',
    preparing: 'Preparing the garden plan',
    active: 'The garden district is taking root',
  },
  celestial_observatory: {
    label: 'Celestial Observatory',
    preparing: 'Preparing the observatory',
    active: 'The observatory is taking shape',
  },
} as const;

export function civicFamilyPresentation(code: string | undefined) {
  return (
    families[code as keyof typeof families] ?? {
      label: 'Shared expedition',
      preparing: 'Preparing your shared goal',
      active: 'Your shared goal is taking shape',
    }
  );
}

export function civicProgressionBand(band: string | undefined) {
  switch (band) {
    case 'coordinated':
      return {
        label: 'Coordinated',
        description: 'An ordered plan: earlier contributions open later paths.',
      };
    case 'mastery':
      return {
        label: 'Mastery',
        description: 'A deeper challenge with combined glyph rules and constraints.',
      };
    default:
      return {
        label: 'Introductory',
        description: 'Meet this family through three distinct restoration paths.',
      };
  }
}

/** Presentation of confirmed facts only: no clock, rewards, project phase or local progress. */
export function civicExpeditionPresentation(board: CivicExpeditionBoard | undefined) {
  const current =
    board?.expeditions.find((expedition) => expedition.status === 'active') ??
    board?.expeditions[0];
  const restored = Object.fromEntries(
    roles.map((code) => {
      const role = current?.workstreams.find((candidate) => candidate.code === code);
      return [code, Boolean(role && role.target > 0 && role.current >= role.target)];
    }),
  ) as Record<ExpeditionWorkstream, boolean>;
  const completedRoles = roles.filter((code) => restored[code]).length;
  const stage = !current
    ? 'idle'
    : current.status !== 'active'
      ? current.status
      : completedRoles === 0
        ? 'preparing'
        : 'active';
  const ready =
    current?.status === 'active' && current.enrolled
      ? current.workstreams.find((role) => !restored[role.code] && role.can_contribute)
      : undefined;
  const family = civicFamilyPresentation(current?.family_code ?? 'waterways');
  const title = {
    idle: 'An expedition awaits',
    preparing: family.preparing,
    active: family.active,
    completed:
      current?.outcome?.title ??
      (current?.family_code === 'waterways' ? 'Waterways restored together' : 'Restored together'),
    expired: 'The expedition has ended',
  }[stage];
  const nextAction = !current
    ? board?.can_start
      ? 'Open a seven-day goal for your Tribe.'
      : 'A leader or officer can open your next shared goal.'
    : stage === 'completed'
      ? 'Your shared achievement remains in the city chronicle.'
      : stage === 'expired'
        ? 'Completed paths are kept in your expedition history.'
        : !current.enrolled
          ? 'Join first, then choose a path to help restore.'
          : ready
            ? `Your ${ready.title} achievement is ready to contribute.`
            : 'Choose an unfinished path. Every role can be played without GPS.';
  const history =
    board?.expeditions.filter(
      (expedition) => expedition.id !== current?.id && expedition.status !== 'active',
    ) ?? [];
  const retainedOutcomes = (board?.expeditions ?? [])
    .filter((entry) => entry.status === 'completed' && entry.outcome)
    .slice(0, 3);
  return {
    current,
    restored,
    completedRoles,
    stage,
    title,
    nextAction,
    history,
    retainedOutcomes,
    familyLabel: family.label,
  };
}

export type CivicExpeditionPresentation = ReturnType<typeof civicExpeditionPresentation>;
